import router from '@/router';
import {Breadcrumb} from '@/services/models';

const DEFAULT_SEO = (window.UMBRACO.seo || []).find(s => s.MetaUrl == '/');

/**
 * Get route object by given route, name or path.
 * @param {String|RouteConfig} key - The route, name or path.
 * @returns {RouteConfig} Route config
 */
export const getRoute = key => {
   const {name = key, path} = Object(key);

   return router.options.routes
   .map(route => (Array.isArray(route.children) ? [route, ...route.children] : [route]))
   .flat()
   .find(route => route.name == name || route.path == path);
};

/**
 * Get an Array of all route names from current child route to its parents routes.
 * @param {String} routeName - The route name
 * @returns {Array<String>}
 */
export const getRouteTree = routeName => {
   let routeTree = [],
      routeSplit = routeName && routeName.split('/');

   // Push current and parent routes.
   if (Array.isArray(routeSplit)) {
      for (let i = 0; i < routeSplit.length; i++) {
         routeTree.push(routeSplit.slice(0, i + 1).join('/'));
      }
   }
   return routeTree;
};

/**
 * Get meta value for given route and meta key. If key is omitted, it returns the whole meta object.
 * @param {RouteConfig} route - The route.
 * @returns {Object|any} Route's meta value or object.
 */
export const getMeta = (route, key) => {
   if (typeof route === 'string') route = getRoute(route);
   const meta = Object(Object(route).meta);
   return key ? meta[key] : meta;
};

/**
 * Get breadcrumb items by given route name. The frontpage is appended as first breadcrumb unless route is frontpage.
 * @param {String} routeName - The route name to get the breadcrumbs for.
 * @returns {Array<Breadcrumb>} Array of breadcrumbs.
 */
export const getBreadcrumbs = routeName => {
   return getRouteTree(routeName).reduce(
      (breadcrumbs, name) => {
         let breadcrumbMeta = getMeta(name, 'breadcrumb');
         const breadcrumb = typeof breadcrumbMeta === 'function' ? breadcrumbMeta() : breadcrumbMeta;
         if (breadcrumb instanceof Breadcrumb) {
            passChainIdToBreadcrumb(breadcrumb);
            breadcrumbs.push(breadcrumb);
         }

         return breadcrumbs;
      },
      routeName == 'frontpage' ? [] : [getMeta('frontpage', 'breadcrumb')]
   ); // Append frontpage as first breadcrumb
};

/**
 * Check if chainId exist in URL and pass to breadcrumb in case it does
 * @param {Breadcrumb} breadcrumb object
 */
function passChainIdToBreadcrumb(breadcrumb) {
   if (breadcrumb.to.includes("/minside")) {
      if (router.history.current.query.chainId && !breadcrumb.to.includes('chainId'))
         breadcrumb.to += '?chainId=' + router.history.current.query.chainId;
   }
}

/**
 * Check if current user has access to provided route.
 * @param {RouteConfig} route - The route to check.
 * @returns {Boolean}
 */
export const hasPermissionToRoute = route => {
   if (getMeta(route, 'authenticated')) {
      return !!router.app.$user.onlineSluttKundeId;
   }

   return true;
};

export const forceLoginForModule = modulename => {
   if (/^mypage/.test(modulename)) {
      return true;
   }
   return false;
};

/**
 * Apply SEO settings for current route (provided).
 * @param {RouteConfig} route - The current route
 */
export const setSEOForRoute = route => {
   if (route) {
      // Try to find the best matched SEO for current route. Fallback to default SEO.
      const seo = router.app.$umbraco.seo.filter(s => route.path.startsWith(s.MetaUrl)).reduce((bestMatch, s) => (bestMatch.MetaUrl.length > s.MetaUrl.length ? bestMatch : s)) || DEFAULT_SEO;
      if (seo) {
         // Title
         document.title = seo.MetaTitle;
         document.querySelector('meta[itemprop="name"]').setAttribute('content', seo.MetaTitle);
         document.querySelector('meta[property="og:title"]').setAttribute('content', seo.MetaTitle);
         document.querySelector('meta[name="twitter:title"]').setAttribute('content', seo.MetaTitle);

         // Description
         document.querySelector('meta[name="description"]').setAttribute('content', seo.MetaDescription);
         document.querySelector('meta[itemprop="description"]').setAttribute('content', seo.MetaDescription);
         document.querySelector('meta[property="og:description"]').setAttribute('content', seo.MetaDescription);
         document.querySelector('meta[name="twitter:description"]').setAttribute('content', seo.MetaDescription);

         // Image
         const image = Object((Object(seo.OpenGraphImage).ImageCrops || []).find(i => i.Alias == 'FacebookOpenGraph')).CropUrl || '';
         document.querySelector('meta[itemprop="image"]').setAttribute('content', image);
         document.querySelector('meta[property="og:image"]').setAttribute('content', image);
         document.querySelector('meta[name="twitter:image"]').setAttribute('content', image);

         // Url
         document.querySelector('meta[property="og:url"]').setAttribute('content', location.href);
      }
   } else console.warn("Can't set SEO for route. Missing `route` parameter");
};
